import { ExpandableSearch, Selector, type SelectorOption } from "@somethings-ui/web";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { ShieldCheck, UsersRound } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

interface SupportListHeaderProps {
  canSeeTeens: boolean;
  onSwitchToTeens: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  showNewChat: boolean;
  onNewChat: () => void;
  supportUnread?: boolean;
  teensUnread?: boolean;
}

export function SupportListHeader({
  canSeeTeens,
  onSwitchToTeens,
  searchQuery,
  onSearchChange,
  showNewChat,
  onNewChat,
  supportUnread = false,
  teensUnread = false,
}: SupportListHeaderProps) {
  const [searchExpanded, setSearchExpanded] = useState(false);

  const options: SelectorOption[] = canSeeTeens
    ? [
        {
          value: "teens",
          label: "Teens",
          icon: <UsersRound className="size-[18px]" aria-hidden />,
          unread: teensUnread,
        },
        {
          value: "support",
          label: "Support",
          icon: <ShieldCheck className="size-[18px]" aria-hidden />,
          unread: supportUnread,
        },
      ]
    : [{ value: "support", label: "Support" }];

  return (
    <header className="relative flex h-14 shrink-0 items-center border-b border-surfaces-z4 px-3">
      {canSeeTeens ? (
        <Selector
          options={options}
          value="support"
          onChange={(next) => {
            if (next === "teens") onSwitchToTeens();
          }}
          aria-label="Select workspace"
          triggerClassName={cn(
            "transition-opacity duration-150",
            searchExpanded && "pointer-events-none opacity-0",
          )}
        />
      ) : (
        <span
          className={cn(
            "truncate py-1 pl-2 font-avantt text-xl font-bold tracking-[-0.2px] text-text-primary transition-opacity duration-150",
            searchExpanded && "opacity-0",
          )}
        >
          Support
        </span>
      )}

      <ExpandableSearch
        value={searchQuery}
        onChange={onSearchChange}
        expanded={searchExpanded}
        onExpandedChange={setSearchExpanded}
        placeholder="Search support"
        className={cn(
          "absolute top-1/2 -translate-y-1/2",
          showNewChat ? "right-14" : "right-3",
          searchExpanded
            ? showNewChat
              ? "w-[calc(100%-4.25rem)]"
              : "w-[calc(100%-1.5rem)]"
            : "w-10",
        )}
      />

      {showNewChat ? (
        <button
          type="button"
          onClick={onNewChat}
          aria-label="New chat"
          className="absolute right-3 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full text-text-secondary hover:bg-surfaces-z4"
        >
          <PencilSquareIcon className="size-5" />
        </button>
      ) : null}
    </header>
  );
}
